import axios from 'axios';
import { User } from '@/interfaces/User'
import { Clase } from '@/interfaces/Clase'
import type { MuroPage } from '@/interfaces/Muro'
import tools from "@/composables/tools";
import { currentUserStore } from '@/stores/currentUser';

export interface LineaCompraRepetible {
    clase_id: number;
    medio_mes: boolean;
    en_pareja: boolean;
    resumen: string;
}

export interface UltimaCompraRepetible {
    disponible: boolean;
    /** Por qué no se propone nada: 'ya_pago_este_mes' | 'sin_compra_reciente' | 'sin_actividades_regulares'. */
    motivo?: string;
    cart_id?: number;
    pagado_el?: string;
    lineas?: LineaCompraRepetible[];
}

export abstract class UserServices {

    static async save(myUser: User): Promise<User | undefined> {
        try {
            const response = await axios.post('user', myUser)
            tools().toast('Se han actualizado los datos correctamente', 'success', 5000)
            return response.data
        } catch (error) {
            tools().toast('No se ha podido guardar los datos del usuario', 'danger', 5000)
        }
    }

    static async loadClases() {
        try {
            const response = await axios.get('user/clases')
            response.data.sort((a: any, b: any) => (a.next_at > b.next_at) ? 1 : ((b.next_at > a.next_at) ? -1 : 0))
            currentUserStore().$state.clases = response.data


        } catch (error) {
            tools().setLog('No se han podido cargar tus clases', 'error')
            tools().toast('No se han podido cargar tus clases', 'danger', 5000)
            return []
        }
    }

    static async getClasesARecuperar(): Promise<Clase[]> {
        try {
            const response = await axios.get('user/getClasesARecuperar')
            return response.data
        } catch (error) {
            tools().setLog('No se han podido cargar tus clases', 'error')
            tools().toast('No se han podido cargar tus clases', 'danger', 5000)
            return {} as Clase[]
        }
    }

    static async getUltimasRecuperaciones(): Promise<Clase[]> {
        try {
            const response = await axios.get('user/getUltimasRecuperaciones')
            return response.data
        } catch (error) {
            tools().setLog('No se han podido cargar la últimas recuperaciones', 'error')
            tools().toast('No se han podido cargar la últimas recuperaciones', 'danger', 5000)
            return {} as Clase[]
        }
    }

    static async getClasesAsistidas(): Promise<Clase[]> {
        try {
            const response = await axios.get('user/getClasesAsistidas')
            return response.data
        } catch (error) {
            tools().setLog('No se han podido cargar las clases asistidas', 'error')
            tools().toast('No se han podido cargar las clases asistidas', 'danger', 5000)
            return [] as Clase[]
        }
    }

    /** Actividades a las que asistió en los últimos 2 meses (para prueba vs clase suelta). */
    static async getClaseIdsAsistidasRecientes(): Promise<number[]> {
        try {
            const response = await axios.get('user/clases-asistidas-recientes', { skipErrorToast: true })
            const ids = response.data?.clase_ids
            return Array.isArray(ids) ? ids.map((id: number) => Number(id)).filter((id: number) => id > 0) : []
        } catch (error) {
            tools().setLog('No se han podido cargar las asistencias recientes', 'error')
            return []
        }
    }

    /**
     * Última compra de actividades que se le puede proponer repetir al alumno.
     * Es una sugerencia: si falla, se sigue sin proponer nada y sin molestar con avisos.
     */
    static async getUltimaCompraRepetible(): Promise<UltimaCompraRepetible> {
        try {
            const response = await axios.get('user/ultima-compra-repetible', { skipErrorToast: true })
            return response.data as UltimaCompraRepetible
        } catch (error) {
            tools().setLog('No se ha podido consultar la última compra repetible', 'error')
            return { disponible: false }
        }
    }

    static setCart() {
        try {
            axios.post('user/cart', currentUserStore().$state.cart)
        } catch (error) {
            tools().setLog('Error al guardar el carrito en el servidor', 'error')
            tools().toast('No se han podido actualizar el carrito', 'danger', 5000)
            return []
        }
    }

    static async loadCart() {
        try {
            const response = await axios.get('user/cart')
            if (response.data.length > 0)
                currentUserStore().$state.cart = response.data
        } catch (error) {
            tools().setLog('Error al traer el carrito desde servidor', 'error')
            tools().toast('No se han podido mostrar el carrito', 'danger', 5000)
            return []
        }
    }

    static async getCurrentUser(): Promise<User | undefined> {
        try {
            const response = await axios.get('/user')
            const userData: User = response.data

            // Actualizar el store con los datos más recientes
            currentUserStore().update(userData)

            // Actualizar también el localStorage
            localStorage.setItem('user', JSON.stringify(userData))

            tools().setLog('Datos del usuario actualizados correctamente', 'info')

            return userData
        } catch (error) {
            tools().setLog('Error al obtener los datos del usuario', 'error')
            tools().toast('No se han podido cargar los datos del usuario', 'danger', 5000)
            return undefined
        }
    }

    static async getMuro(cursor?: string | null): Promise<MuroPage> {
        try {
            const response = await axios.get('user/muro', {
                params: cursor ? { cursor } : {},
                skipErrorToast: true,
            })
            const data = Array.isArray(response.data?.data) ? response.data.data : []
            return {
                data,
                next_cursor: response.data?.next_cursor ?? null,
                has_more: Boolean(response.data?.has_more),
            }
        } catch (error) {
            tools().setLog('No se pudo cargar el muro de inicio', 'error')
            return { data: [], next_cursor: null, has_more: false }
        }
    }

    static async registrarMuroVistos(items: Array<{ id: string; tipo: string }>): Promise<void> {
        if (!items.length) return
        try {
            await axios.post('user/muro/visto', { items }, { skipErrorToast: true })
        } catch {
            /* no bloquear el muro si falla el recuento */
        }
    }

    static async listarMuroComentarios(
        tipo: 'nota' | 'actividad',
        id: number,
    ): Promise<import('@/interfaces/Muro').MuroComentario[]> {
        try {
            const response = await axios.get('user/muro/comentarios', {
                params: { tipo, id },
                skipErrorToast: true,
            })
            return Array.isArray(response.data?.data) ? response.data.data : []
        } catch {
            return []
        }
    }

    static async crearMuroComentario(
        tipo: 'nota' | 'actividad',
        id: number,
        texto: string,
    ): Promise<import('@/interfaces/Muro').MuroComentario | null> {
        try {
            const response = await axios.post('user/muro/comentarios', { tipo, id, texto })
            return response.data?.data ?? null
        } catch {
            return null
        }
    }

    /** Si las cuotas del alumno ya están domiciliadas (sin datos bancarios). */
    static async getMiDomiciliacion(): Promise<{
        activa: boolean
        pendiente: boolean
        cuenta_enmascarada: string | null
    } | null> {
        try {
            const response = await axios.get('user/mi-domiciliacion', { skipErrorToast: true });
            return {
                activa: Boolean(response.data?.activa),
                pendiente: Boolean(response.data?.pendiente),
                cuenta_enmascarada: response.data?.cuenta_enmascarada ?? null,
            };
        } catch {
            return null;
        }
    }

    static async solicitarMiDomiciliacion(numeroCuenta: string): Promise<{
        ok: boolean
        message: string
        activa?: boolean
        pendiente?: boolean
        cuenta_enmascarada?: string | null
    }> {
        try {
            const response = await axios.put('user/mi-domiciliacion', {
                numero_cuenta: numeroCuenta,
            }, { skipErrorToast: true });
            return {
                ok: true,
                message: String(response.data?.message || 'Cuenta guardada'),
                activa: Boolean(response.data?.activa),
                pendiente: Boolean(response.data?.pendiente),
                cuenta_enmascarada: response.data?.cuenta_enmascarada ?? null,
            };
        } catch (error: any) {
            const message = error?.response?.data?.message
                ? String(error.response.data.message)
                : 'No se pudo guardar el número de cuenta';
            return { ok: false, message };
        }
    }
}
